import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, StyleSheet, TextInput, TextInputKeyPressEvent, useWindowDimensions } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation, usePreventRemove } from '@react-navigation/native';

import AlertTriangleRed from '@/assets/icons/alertTriangleRed';
import IconCircledClose from '@/assets/icons/circled-close';
import { Button } from '@/components/ButtonComponent';
import { InlineTextInput } from '@/components/InlineTextInput';
import { SheetScreen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Pressable } from '@/components/button/Pressable';
import { Box, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { textVariants } from '@/components/lib/typography';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { queryClient } from '@/data';
import { UserAvatar } from '@/feature/account-details/components/UserAvatar';
import { useAvailableAvatars } from '@/feature/account-details/hooks/use-available-avatars';
import { useUpdateAvatarUrl } from '@/feature/account-details/hooks/use-update-avatar-url';
import { useUpdateUsername } from '@/feature/account-details/hooks/use-update-username';
import { getUsernameErrorMessage } from '@/feature/account-details/utils/utils';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { authUserKeys, avatarUrlSelector, useAuthUserConfig, usernameSelector } from '@/hooks/use-auth-user-config';
import { useAuthUserSettings } from '@/hooks/use-auth-user-settings';
import { useShowOneTime } from '@/hooks/use-show-one-time';
import { useUserStore } from '@/hooks/use-user-store';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

const AVATAR_SIZE = 64;
const SPACING = 12;
const SAVE_CHANGES_BUTTON_HEIGHT = 44;
const PADDING_BOTTOM = 16;
const PADDING_RIGHT = 16;
const ICON_WIDTH = 24;
const ICON_MARGIN = 4;
const ICON_AREA = ICON_WIDTH + ICON_MARGIN;

export type UsernameError =
    | { type: 'text'; message: string }
    | { type: 'maxLength'; current: number; max: number }
    | undefined;

const Wrapper = isWeb ? Box : Pressable;

export const EditUsernameAndAvatar = () => {
    const { width } = useWindowDimensions();
    const safeInsets = useSafeAreaInsets();
    const { data } = useAvailableAvatars();
    const { showInfoSheet } = useAlerts();
    const avatars = data?.all_avatar_urls || [];
    const { data: username } = useAuthUserConfig({
        select: usernameSelector,
    });
    const { data: currentAvatarUrl } = useAuthUserConfig({
        select: avatarUrlSelector,
    });
    const { data: userData } = useAuthUserSettings();
    const canResetUsername = userData && userData.can_reset_username;

    const { mutate: updateAvatarUrl } = useUpdateAvatarUrl();
    const { mutate: updateUsername } = useUpdateUsername();
    const navigation = useNavigation();

    const numColumns = Math.floor(width / (AVATAR_SIZE + SPACING));
    const [newUsername, setNewUsername] = useState<string | undefined>(username || '');
    const [newUserAvatarUrl, setNewUserAvatarUrl] = useState<string | undefined | null>(currentAvatarUrl);
    const [usernameError, setUsernameError] = useState<UsernameError | undefined>(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const usernameChanged = username !== newUsername;
    const avatarChanged = currentAvatarUrl !== newUserAvatarUrl;
    const usernameOrAvatarHaveChanged = usernameChanged || avatarChanged;
    const canSave = avatarChanged || (usernameChanged && canResetUsername);
    const usernameLength = newUsername?.length || 0;
    const inputRef = useRef<TextInput>(null);
    const { handleInteraction: handleBannerInteraction } = useShowOneTime('SocialFeed_EditUsernameAvatarBanner');

    // shared values update directly on the UI thread, so the icon hides immediately
    // without waiting for a JS re-render cycle as would happen with regular React state/values
    const iconOpacity = useSharedValue(username ? 1 : 0);
    const iconWidth = useSharedValue(username ? ICON_AREA : 0);
    const iconAnimatedStyle = useAnimatedStyle(() => ({
        width: iconWidth.value,
        opacity: iconOpacity.value,
    }));

    const onUsernameChange = (text: string) => {
        const sanitized = text.replace(/[^a-zA-Z0-9]/g, '');
        iconOpacity.value = sanitized.length > 0 ? 1 : 0;
        iconWidth.value = sanitized.length > 0 ? ICON_AREA : 0;
        if (!sanitized) setUsernameError(undefined);
        setNewUsername(sanitized);
    };

    // onKeyPress fires before onChangeText hiding the icon here reduces the
    // brief flash where the icon is still visible after the last character is deleted.
    const onKeyPress = ({ nativeEvent: { key } }: TextInputKeyPressEvent) => {
        if (key === 'Backspace' && (newUsername?.length ?? 0) <= 1) {
            iconOpacity.value = 0;
            iconWidth.value = 0;
        }
    };

    const handleClear = () => {
        iconOpacity.value = 0;
        iconWidth.value = 0;
        setNewUsername('');
        setUsernameError(undefined);
        inputRef.current?.focus();
    };

    const renderItem = useCallback(
        ({ item }: { item: string }) => (
            <Pressable style={styles.avatarContainer} onPress={() => setNewUserAvatarUrl(item)}>
                <UserAvatar
                    avatarUrl={item}
                    avatarContainerStyles={styles.itemAvatarContainer}
                    avatarStyles={styles.itemAvatar}
                />
            </Pressable>
        ),
        []
    );

    const handleOnCloseScreen = useCallback(() => {
        if (Keyboard.isVisible()) Keyboard.dismiss();
        navigation.goBack();
    }, [navigation]);

    const onSaveChanges = () => {
        if (!usernameOrAvatarHaveChanged || !newUsername || !newUserAvatarUrl) {
            return;
        }
        setIsSaving(true);
        setUsernameError(undefined);

        const handleSuccess = async () => {
            setIsSaving(false);

            queryClient.invalidateQueries({ queryKey: authUserKeys.config() });
            queryClient.invalidateQueries({ queryKey: authUserKeys.settings() });

            const { actions, session } = useUserStore.getState();
            actions.refresh(session);
            await handleBannerInteraction();
            setIsSaved(true);
        };

        const handleError = (error: any) => {
            setIsSaving(false);
            // BE returns an array of error codes, we use the first one as primary error code
            const mappedError = getUsernameErrorMessage(error.error_codes[0], newUsername);
            setUsernameError(mappedError);
        };

        if (usernameChanged && avatarChanged) {
            updateAvatarUrl(newUserAvatarUrl, {
                onSuccess: () => {
                    updateUsername(newUsername, {
                        onSuccess: handleSuccess,
                        onError: handleError,
                    });
                },
                onError: () => {
                    setIsSaving(false);
                },
            });
            return;
        }
        if (usernameChanged) {
            updateUsername(newUsername, {
                onSuccess: handleSuccess,
                onError: handleError,
            });
            return;
        }
        if (avatarChanged) {
            updateAvatarUrl(newUserAvatarUrl, {
                onSuccess: handleSuccess,
                onError: () => {
                    setIsSaving(false);
                },
            });
        }
    };

    useEffect(() => {
        if (isSaved) navigation.goBack();
    }, [isSaved, navigation]);

    const onPreventRemove = useCallback(
        ({ data: { action } }: { data: { action: Parameters<typeof navigation.dispatch>[0] } }) => {
            showInfoSheet({
                title: 'Discard changes?',
                description: "If you leave now, your edits won't be saved.",
                buttonLabel: 'Keep editing',
                secondaryLabel: 'Discard',
                handleSecondaryPress: () => navigation.dispatch(action),
            });
        },
        [showInfoSheet, navigation]
    );

    usePreventRemove(usernameOrAvatarHaveChanged && !isSaved, onPreventRemove);

    return (
        <SheetScreen style={common.flex} edges={['top']}>
            <Box flex={1} backgroundColor={ds.colorToken.background.neutral.base}>
                <ScreenNavBar
                    sheet
                    title={'Username & Avatar'}
                    closeIconMode={'close'}
                    onClose={handleOnCloseScreen}
                    titleOverrideStyle={styles.title}
                />
                <Wrapper style={common.flex} onPress={isWeb ? undefined : Keyboard.dismiss}>
                    <Box px={'s16'} marginVertical={'s8'} backgroundColor={ds.colorToken.background.neutral.base}>
                        <Box alignItems={'center'} paddingBottom={'s20'}>
                            <UserAvatar
                                avatarUrl={newUserAvatarUrl}
                                avatarContainerStyles={styles.accountDetailsContainerAvatar}
                                avatarStyles={styles.accountDetailsAvatar}
                            />
                            <SizedBox value={12} />

                            <InlineTextInput
                                ref={inputRef}
                                value={newUsername ?? ''}
                                onChangeText={onUsernameChange}
                                onKeyPress={onKeyPress}
                                textInputStyle={{ maxWidth: width - PADDING_RIGHT - ICON_AREA }}
                                placeholder={'Enter a username'}
                                returnKeyType={'done'}
                                autoCorrect={false}
                                selectionColor={ds.colorToken.text.brand}
                                editable={canResetUsername ?? false}
                                onFocus={() => {
                                    if (!isWeb) {
                                        inputRef.current?.setSelection(usernameLength, usernameLength);
                                    }
                                }}
                                onBlur={() => {
                                    if (!newUsername) {
                                        const restored = username || '';
                                        iconOpacity.value = restored ? 1 : 0;
                                        iconWidth.value = restored ? ICON_AREA : 0;
                                        setNewUsername(restored);
                                    }
                                }}
                                rightIcon={
                                    canResetUsername ? (
                                        <Animated.View style={[styles.clearIconContainer, iconAnimatedStyle]}>
                                            <TouchableBox onPress={handleClear}>
                                                <IconCircledClose width={ICON_WIDTH} height={ICON_WIDTH} />
                                            </TouchableBox>
                                        </Animated.View>
                                    ) : null
                                }
                                helperLabel={
                                    canResetUsername ? 'Username' : 'You reached your limit of 1 username change.'
                                }
                                hasError={!!usernameError}
                                errorText={usernameError ? <ErrorMessage error={usernameError} /> : undefined}
                            />
                        </Box>
                    </Box>
                    <Box flex={1}>
                        <FlatList
                            keyExtractor={item => item}
                            contentContainerStyle={[
                                common.paddingHorizontal,
                                { paddingBottom: safeInsets.bottom },
                                common.alignCenter,
                            ]}
                            columnWrapperStyle={{ gap: SPACING }}
                            numColumns={numColumns}
                            data={avatars}
                            keyboardDismissMode={'on-drag'}
                            renderItem={renderItem}
                        />
                        <Box width={'100%'} height={40} position={'absolute'} bottom={0}>
                            <LinearGradient
                                angle={180}
                                style={common.flex}
                                colors={[
                                    designSystem.colors.neutral900,
                                    withOpacity(designSystem.colors.neutral900, 0.96),
                                    withOpacity(designSystem.colors.neutral900, 0.83),
                                    withOpacity(designSystem.colors.neutral900, 0.4),
                                    withOpacity(designSystem.colors.neutral900, 0),
                                ]}
                                positions={[0, 0.25, 0.496, 0.7475, 1]}
                            />
                        </Box>
                    </Box>
                    {canResetUsername ? (
                        <Text variant={'bodySmall'} color={'neutral300'} textAlign={'center'}>
                            Your username can only be changed once
                        </Text>
                    ) : null}
                    <Box
                        style={{ paddingBottom: safeInsets.bottom + PADDING_BOTTOM }}
                        paddingHorizontal={'s16'}
                        paddingTop={'s16'}
                    >
                        <Button
                            label={'Save'}
                            hierarchy={'primary'}
                            onPress={onSaveChanges}
                            style={styles.actionButton}
                            textStyle={textVariants.titleMedium}
                            disabled={!canSave}
                            loading={isSaving}
                            disableTextStyle={{ color: designSystem.colors.neutral400 }}
                        />
                    </Box>
                </Wrapper>
            </Box>
        </SheetScreen>
    );
};

const ErrorMessage = ({ error }: { error?: UsernameError }) => {
    return (
        <Row alignItems={'center'} marginTop={'s2'} height={20}>
            <AlertTriangleRed width={16} height={16} />
            {error?.type === 'text' ? (
                <Text variant={'bodySmall'} color={ds.colorToken.text.danger} style={styles.errorText}>
                    {error.message}
                </Text>
            ) : (
                <Text style={styles.errorText}>
                    <Text variant={'bodySmall'} color={ds.colorToken.text.danger}>
                        Max character limit · {error?.current}
                    </Text>
                    <Text variant={'bodySmall'} color="neutral300">
                        {' / '}
                        {error?.max}
                    </Text>
                </Text>
            )}
        </Row>
    );
};

const styles = StyleSheet.create({
    title: {
        color: ds.colorValue.text.primary,
    },
    avatarContainer: {
        marginBottom: SPACING,
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
    },
    accountDetailsContainerAvatar: {
        padding: 26,
        borderRadius: 100,
        width: 112,
        height: 112,
    },
    accountDetailsAvatar: {
        width: 64,
        height: 64,
    },
    itemAvatarContainer: {
        width: 64,
        height: 64,
        borderRadius: 18,
    },
    itemAvatar: {
        width: 32,
        height: 32,
    },
    clearIconContainer: {
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
    },
    actionButton: {
        height: SAVE_CHANGES_BUTTON_HEIGHT,
        paddingVertical: 10,
    },
    errorText: {
        marginLeft: 4,
    },
});
